import { useState } from "react"
import { Alert, Button, MenuItem, Paper, Stack, TextField } from "@mui/material"
import { validUrl } from "../markup/shared"
import type { SlotOccurrence, UrlButton, UrlButtonStyle } from "../types"
import { isUrlButtonStyle } from "./urlButtonStyle"
import {
  initialDestination,
  LinkDestinationFields,
  resolveDestination,
  SourceTextField,
  SlotOccurrenceList,
} from "../editor/messageLinkDialog"
import type { EditorSlot } from "../editor/linkEditing"

export interface UrlButtonDialogProps {
  value?: UrlButton
  slots?: EditorSlot[]
  occurrences?: SlotOccurrence[]
  onSelectOccurrence?: (occurrence: SlotOccurrence) => void
  onCreateSlot?: () => string
  readOnly?: boolean
  onSave: (value: UrlButton) => void
  onClose: () => void
}

/** Inline local draft, intentionally not a form: hosts already own a form. */
export function UrlButtonDialog({
  value,
  slots,
  occurrences = slots?.flatMap((slot) => slot.occurrences) ?? [],
  onSelectOccurrence,
  onCreateSlot,
  readOnly,
  onSave,
  onClose,
}: UrlButtonDialogProps) {
  const [text, setText] = useState(value?.text ?? "")
  const [style, setStyle] = useState<UrlButtonStyle | "">(value?.style ?? "")
  const [destination, setDestination] = useState(() =>
    initialDestination(value?.url ?? ""),
  )
  const [error, setError] = useState("")
  const [textError, setTextError] = useState("")
  const save = () => {
    if (readOnly) return
    if (!text.trim()) {
      setTextError("Введите текст кнопки")
      return
    }
    if (destination.kind === "static" && !validUrl(destination.url)) {
      setError("Укажите HTTP(S) URL")
      return
    }
    try {
      onSave({
        text,
        url: resolveDestination(destination, onCreateSlot),
        ...(style ? { style } : {}),
      })
      onClose()
    } catch {
      setError("Не удалось создать ссылку")
    }
  }
  return (
    <Paper
      variant="outlined"
      role="region"
      aria-label="Настройки URL-кнопки"
      sx={{ p: 1.5, maxWidth: 440 }}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault()
          event.stopPropagation()
          onClose()
        }
        if (event.key === "Enter" && event.target instanceof HTMLInputElement) {
          event.preventDefault()
          event.stopPropagation()
          save()
        }
      }}
    >
      <Stack spacing={1.5}>
        <SourceTextField
          label="Текст кнопки"
          value={text}
          onChange={(next) => {
            setText(next)
            setTextError("")
          }}
          disabled={readOnly}
          autoFocus
          error={Boolean(textError)}
          helperText={textError}
        />
        <LinkDestinationFields
          value={destination}
          onChange={(next) => {
            setDestination(next)
            setError("")
          }}
          slots={slots}
          disabled={readOnly}
          error={error}
        />
        <TextField
          select
          label="Цвет"
          size="small"
          value={style}
          SelectProps={{ displayEmpty: true }}
          InputLabelProps={{ shrink: true }}
          disabled={readOnly}
          onChange={(event) => {
            const next = event.target.value
            if (!readOnly && (next === "" || isUrlButtonStyle(next)))
              setStyle(next)
          }}
        >
          <MenuItem value="">Стандартный</MenuItem>
          <MenuItem value="primary">Синий</MenuItem>
          <MenuItem value="success">Зелёный</MenuItem>
          <MenuItem value="danger">Красный</MenuItem>
        </TextField>
        {readOnly && <Alert severity="info">Редактирование недоступно</Alert>}
        <SlotOccurrenceList
          occurrences={occurrences.filter(
            (item) =>
              destination.kind === "slot" && item.slotId === destination.id,
          )}
          onSelectOccurrence={onSelectOccurrence}
        />
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="contained" disabled={readOnly} onClick={save}>
            Готово
          </Button>
        </Stack>
      </Stack>
    </Paper>
  )
}
